import React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Shield, Lock, ChevronRight } from 'lucide-react';
import { systemRoleLabel, systemRoleHint, coveragePct } from '../utils/rbacLabels';
import { IITH } from '@/pages/landing/iithBrand';

function CoverageBar({ count, total }) {
  const pct = coveragePct(count, total);
  return (
    <div className="min-w-[120px]">
      <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-1">
        <span>{count} of {total}</span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: pct > 70 ? IITH.saffron : IITH.navy }}
        />
      </div>
    </div>
  );
}

/** Informative roles directory table with permission drill-down. */
export default function RolesTable({ roles, loading, permissionTotal, onToggle, onViewPermissions }) {
  if (loading) {
    return (
      <div className="rounded-xl border bg-white p-12 text-center text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }

  return (
    <section className="rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="roles-table">
      <div className="px-5 py-4 border-b bg-zinc-50/80">
        <h2 className="text-sm font-semibold text-zinc-900">Role directory</h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          Click a role or View permissions to drill into the granted capability list.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/50 text-left text-[11px] uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-5 py-3 font-medium">Role</th>
              <th className="px-5 py-3 font-medium">App access</th>
              <th className="px-5 py-3 font-medium">Coverage</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => {
              const count = (r.permission_codes || []).length;
              const active = r.is_active !== false;
              return (
                <tr
                  key={r.role_key}
                  className="border-t hover:bg-zinc-50/60 cursor-pointer"
                  data-testid={`role-row-${r.role_key}`}
                  onClick={() => onViewPermissions?.(r)}
                >
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-zinc-900 flex items-center gap-2">
                      <Shield className="h-3.5 w-3.5 shrink-0" style={{ color: IITH.saffron }} />
                      {r.name}
                      {r.is_system ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#E8EEF5] text-[#071B34]">
                          <Lock className="h-2.5 w-2.5" /> System
                        </span>
                      ) : null}
                    </div>
                    {r.description ? (
                      <p className="text-[11px] text-zinc-500 mt-1 max-w-md leading-relaxed">{r.description}</p>
                    ) : null}
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-zinc-800">{systemRoleLabel(r.system_role)}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{systemRoleHint(r.system_role)}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <CoverageBar count={count} total={permissionTotal || Math.max(count, 1)} />
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                      active ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'
                    }`}>
                      {active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewPermissions?.(r)}
                        data-testid={`role-view-perms-${r.role_key}`}
                      >
                        Permissions <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onToggle(r)}>
                        {active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
